import { useState, useEffect, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { X, Moon, Scale, Pencil, Check } from 'lucide-react'
import { SneakerIcon } from '../SneakerIcon'
import { toast } from 'sonner'
import { SLEEP_QUALITY_OPTIONS, type SleepQuality } from '@tfa/shared'

const F = 'Roboto, sans-serif'

export interface MetricsValues {
  /** Качество сна: BAD/SO_SO/GOOD или '' если не выбрано */
  sleepQuality: SleepQuality | ''
  steps: string
  weight: string
}

type NumericMetricKey = 'steps' | 'weight'

interface Props {
  open: boolean
  onClose: () => void
  initialValues: MetricsValues
  onSave: (values: MetricsValues) => void
  /** Which input to auto-focus on open (scrolls into view if needed). */
  focusField?: 'sleep' | NumericMetricKey
  /** #289: реально сдано за ВЫБРАННЫЙ день (из записи дня). initialValues может
   *  нести прошлые значения серой маской (новый день) — по ним «сдано» считать
   *  нельзя, иначе поля залочены в новый день. */
  submittedToday?: { sleep: boolean; steps: boolean; weight: boolean }
  /**
   * #400: индивидуальная цель по шагам от куратора. Раньше в подсказке и маске
   * стояли жёсткие 10 000 у всех — «но для каждого ведь индивидуально».
   * Не передана или null — показываем общий ориентир, как и раньше.
   */
  stepsGoal?: number | null
  /**
   * #635: сохранение в пути (в т.ч. повторы после обрыва связи). Кнопка заперта,
   * иначе каждое нажатие шлёт параллельный запрос на ту же запись дня.
   */
  saving?: boolean
}

// Диапазоны должны совпадать с zod-схемой `diaryEntrySchema` в
// packages/api/src/routes/diary.ts (sleep 0–24, steps 0–100000, weight 20–500).
type FieldKind = 'decimal' | 'integer'

const FIELDS: Array<{
  key: NumericMetricKey
  icon: typeof Moon
  label: string
  unit: string
  placeholder: string
  inputMode: 'decimal' | 'numeric'
  color: string
  bg: string
  borderC: string
  hint: string
  min: number
  max: number
  kind: FieldKind
  /** Текст диапазона для inline-ошибки и toast. */
  rangeLabel: string
}> = [
  {
    key: 'steps',
    icon: SneakerIcon,
    label: 'Шаги',
    unit: 'шаг',
    placeholder: '10 000',
    inputMode: 'numeric',
    color: '#4A9FE5',
    bg: 'rgba(74,159,229,0.07)',
    borderC: 'rgba(74,159,229,0.25)',
    hint: 'Цель: 10 000',
    min: 0,
    max: 100000,
    kind: 'integer',
    rangeLabel: '0–100 000 шагов',
  },
  {
    key: 'weight',
    icon: Scale,
    label: 'Вес',
    unit: 'кг',
    placeholder: '78.5',
    inputMode: 'decimal',
    color: '#34C759',
    bg: 'rgba(52,199,89,0.07)',
    borderC: 'rgba(52,199,89,0.25)',
    hint: 'Утром, натощак',
    min: 20,
    max: 500,
    kind: 'decimal',
    rangeLabel: '20–500 кг',
  },
]

// Разрешаем только цифры (+ одну точку или запятую для decimal-полей).
// Разделитель сохраняется в том виде, как ввёл пользователь — нормализация
// в точку происходит уже при parseFloat (см. parseDecimal). Целочисленные
// поля (Шаги) запрещают разделитель, чтобы не допустить «10 000.5» из ошибки
// клавиатурной раскладки.
function sanitizeMetricInput(raw: string, kind: FieldKind): string {
  if (kind === 'integer') {
    return raw.replace(/[^\d]/g, '').slice(0, 6) // до 999 999, с запасом над 100 000
  }
  const filtered = raw.replace(/[^\d.,]/g, '')
  const firstSep = filtered.search(/[.,]/)
  const cleaned = firstSep === -1
    ? filtered
    : filtered.slice(0, firstSep + 1) + filtered.slice(firstSep + 1).replace(/[.,]/g, '')
  const sep = firstSep === -1 ? '' : cleaned[firstSep]
  const [intPart = '', decPart = ''] = sep ? cleaned.split(sep) : [cleaned]
  const trimmedInt = intPart.slice(0, 3)
  const trimmedDec = decPart.slice(0, 2) // вес — до сотых (84.15), #194
  if (sep) return `${trimmedInt}${sep}${trimmedDec}`
  return trimmedInt
}

function parseDecimal(raw: string): number {
  return parseFloat(raw.replace(',', '.'))
}

export function MetricsEditSheet({ open, onClose, initialValues, onSave, focusField, submittedToday, stepsGoal, saving = false }: Props) {
  // #400: цель показываем в том же формате, что и дефолт в FIELDS — «10 000».
  const goalText = typeof stepsGoal === 'number' && stepsGoal > 0
    ? stepsGoal.toLocaleString('ru-RU').replace(/\u00A0/g, ' ')
    : null
  // Поля стартуют ПУСТЫМИ (bug #215): подтянутые с прошлого раза значения — это
  // лишь подсказка (серый плейсхолдер), а не сданные данные. Не введёшь сам →
  // не сохраняется и не идёт в XP. initialValues используем как маску-плейсхолдер.
  const EMPTY: MetricsValues = { sleepQuality: '', steps: '', weight: '' }
  const [vals, setVals] = useState<MetricsValues>(EMPTY)
  const inputRefs = useRef<Record<NumericMetricKey, HTMLInputElement | null>>({ steps: null, weight: null })
  const sleepCardRef = useRef<HTMLDivElement | null>(null)

  // #2: сданные сегодня показатели — залочены (вид «сдано»), меняются только через
  // карандаш (тихо: правка без XP и без TG). editing[key] = поле разблокировано.
  const [editing, setEditing] = useState<{ sleep: boolean; steps: boolean; weight: boolean }>({ sleep: false, steps: false, weight: false })
  // #289: «сдано» — строго по факту записи ВЫБРАННОГО дня (submittedToday), а не по
  // initialValues (в новый день там прошлые значения-маска → иначе поля залочены).
  const submitted = submittedToday ?? { sleep: false, steps: false, weight: false }
  const startEdit = (key: 'sleep' | 'steps' | 'weight') => {
    setEditing((p) => ({ ...p, [key]: true }))
    setVals((p) => key === 'sleep'
      ? { ...p, sleepQuality: initialValues.sleepQuality }
      : { ...p, [key]: String(initialValues[key] ?? '') })
  }

  useEffect(() => {
    if (open) {
      setVals(EMPTY)
      setEditing({ sleep: false, steps: false, weight: false })
    }
  }, [open])

  // Auto-focus the requested field once the sheet is open (wait for enter animation)
  useEffect(() => {
    if (!open || !focusField) return
    const t = setTimeout(() => {
      if (focusField === 'sleep') {
        sleepCardRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        return
      }
      const el = inputRefs.current[focusField]
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        el.focus({ preventScroll: true })
        el.select()
      }
    }, 320)
    return () => clearTimeout(t)
  }, [open, focusField])

  const errors = useMemo<Partial<Record<NumericMetricKey, string>>>(() => {
    const out: Partial<Record<NumericMetricKey, string>> = {}
    for (const f of FIELDS) {
      const raw = vals[f.key].trim()
      if (raw === '') continue
      const v = f.kind === 'decimal' ? parseDecimal(raw) : parseFloat(raw)
      if (!Number.isFinite(v)) {
        out[f.key] = 'Введите число'
        continue
      }
      if (v < f.min || v > f.max) {
        out[f.key] = `${f.label}: допустимо ${f.rangeLabel}`
      }
    }
    return out
  }, [vals])

  const errorList = useMemo(() => Object.values(errors).filter(Boolean) as string[], [errors])
  const hasErrors = errorList.length > 0
  const canSave = !hasErrors && !saving

  function handleSave() {
    if (saving) return
    if (hasErrors) {
      toast.error(errorList[0])
      return
    }
    // Hand off to the parent — the parent owns the mutation and decides when
    // to close the sheet (on success) or keep it open (on error).
    onSave(vals)
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="modal-metrics"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(30px)',
              borderTopLeftRadius: 26,
              borderTopRightRadius: 26,
              paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
            }}
          >
            {/* Handle */}
            <div
              style={{
                width: 40,
                height: 4,
                borderRadius: 2,
                background: '#D1D5DB',
                margin: '10px auto 0',
              }}
            />

            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 20px 8px',
              }}
            >
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1C2D41', margin: '0 0 2px' }}>
                  Показатели дня
                </h2>
                <p style={{ fontSize: 13, color: '#9AA5B4', margin: 0 }}>
                  Заполни или обнови данные
                </p>
              </div>
              <button
                data-testid="btn-close-metrics"
                onClick={onClose}
                style={{
                  background: 'rgba(0,0,0,0.06)',
                  border: 'none',
                  borderRadius: '50%',
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>
            </div>

            {/* Fields */}
            <div
              style={{
                padding: '8px 20px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {/* Sleep quality — 3-button select (BAD / SO_SO / GOOD) */}
              <div
                ref={sleepCardRef}
                style={{
                  borderRadius: 16,
                  padding: '12px 14px',
                  background: 'rgba(139,127,245,0.07)',
                  border: '1px solid rgba(139,127,245,0.25)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 13,
                    background: 'rgba(139,127,245,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Moon size={19} color="#8B7FF5" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1C2D41' }}>Качество сна</div>
                    <div style={{ fontSize: 11, color: '#B0BEC5', marginTop: 2 }}>Как ты себя чувствуешь сейчас?</div>
                  </div>
                </div>
                {submitted.sleep && !editing.sleep ? (
                  // #2: сон сдан — залочен, вид «сдано» + карандаш для тихой правки.
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(52,199,89,0.08)', border: '1px solid rgba(52,199,89,0.25)' }}>
                    <Check size={16} color="#34C759" />
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#1C2D41' }}>
                      {SLEEP_QUALITY_OPTIONS.find((o) => o.value === initialValues.sleepQuality)?.fullLabel ?? 'Сдано'}
                    </span>
                    <button type="button" data-testid="edit-sleep" onClick={() => startEdit('sleep')} aria-label="Изменить" style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: '#8B7FF5', display: 'inline-flex' }}>
                      <Pencil size={15} />
                    </button>
                  </div>
                ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {SLEEP_QUALITY_OPTIONS.map((opt) => {
                    const selected = vals.sleepQuality === opt.value
                    return (
                      <button
                        key={opt.value}
                        data-testid={`sleep-quality-${opt.value}`}
                        data-selected={selected ? 'true' : 'false'}
                        type="button"
                        onClick={() => setVals((prev) => ({ ...prev, sleepQuality: opt.value }))}
                        style={{
                          width: '100%',
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '12px 14px',
                          borderRadius: 12,
                          background: selected ? `${opt.color}14` : 'rgba(255,255,255,0.85)',
                          border: `1.5px solid ${selected ? opt.color : 'rgba(26,58,92,0.10)'}`,
                          color: '#1C2D41',
                          fontFamily: F,
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'background 0.15s, border-color 0.15s',
                        }}
                      >
                        {/* Цветной индикатор-обводка с заливкой при выборе */}
                        <span
                          aria-hidden
                          style={{
                            width: 18, height: 18, borderRadius: '50%',
                            border: `2px solid ${opt.color}`,
                            background: selected ? opt.color : 'transparent',
                            flexShrink: 0,
                            transition: 'background 0.15s',
                          }}
                        />
                        <span style={{ flex: 1, fontSize: 14, fontWeight: selected ? 700 : 500 }}>
                          {opt.fullLabel}
                        </span>
                      </button>
                    )
                  })}
                </div>
                )}
              </div>

              {FIELDS.map((f) => {
                const fieldError = errors[f.key]
                const inputBorder = fieldError
                  ? '#FF3B30'
                  : vals[f.key]
                    ? f.color + '66'
                    : f.borderC
                return (
                  <div
                    key={f.key}
                    style={{
                      borderRadius: 16,
                      padding: '12px 14px',
                      background: fieldError ? 'rgba(255,59,48,0.06)' : f.bg,
                      border: `1px solid ${fieldError ? 'rgba(255,59,48,0.25)' : f.borderC}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {/* Icon */}
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 13,
                          background: `${f.color}1A`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <f.icon size={19} color={f.color} />
                      </div>
                      {/* Right side */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 6,
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1C2D41' }}>
                            {f.label}
                          </span>
                          <span style={{ fontSize: 11, color: '#B0BEC5' }}>
                            {f.key === 'steps' && goalText ? `Цель: ${goalText}` : f.hint}
                          </span>
                        </div>
                        {submitted[f.key] && !editing[f.key] ? (
                          // #2: показатель сдан — залочен, вид «сдано» + карандаш для тихой правки.
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 12, background: 'rgba(52,199,89,0.08)', border: '1px solid rgba(52,199,89,0.25)' }}>
                            <Check size={16} color="#34C759" />
                            <span style={{ flex: 1, fontSize: 17, fontWeight: 700, color: '#1C2D41' }}>
                              {initialValues[f.key]} <span style={{ fontSize: 13, fontWeight: 500, color: '#9AA5B4' }}>{f.unit}</span>
                            </span>
                            <button type="button" data-testid={`edit-${f.key}`} onClick={() => startEdit(f.key)} aria-label="Изменить" style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: f.color, display: 'inline-flex' }}>
                              <Pencil size={15} />
                            </button>
                          </div>
                        ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            data-testid={`input-metric-${f.key}`}
                            ref={(el) => { inputRefs.current[f.key] = el }}
                            value={vals[f.key]}
                            onChange={(e) =>
                              setVals((prev) => ({ ...prev, [f.key]: sanitizeMetricInput(e.target.value, f.kind) }))
                            }
                            inputMode={f.inputMode}
                            pattern={f.kind === 'integer' ? '[0-9]*' : undefined}
                            placeholder={
                              initialValues[f.key]
                                ? String(initialValues[f.key])
                                : (f.key === 'steps' && goalText ? goalText : f.placeholder)
                            }
                            aria-invalid={!!fieldError}
                            style={{
                              flex: 1,
                              height: 40,
                              borderRadius: 12,
                              border: `1.5px solid ${inputBorder}`,
                              background: 'rgba(255,255,255,0.85)',
                              padding: '0 12px',
                              fontSize: 17,
                              fontWeight: 700,
                              color: '#1C2D41',
                              outline: 'none',
                              fontFamily: F,
                              transition: 'border-color 0.15s',
                            }}
                          />
                          <span
                            style={{
                              fontSize: 14,
                              color: '#9AA5B4',
                              fontWeight: 500,
                              flexShrink: 0,
                              minWidth: 28,
                            }}
                          >
                            {f.unit}
                          </span>
                        </div>
                        )}
                        {fieldError && (
                          <div
                            data-testid={`error-metric-${f.key}`}
                            style={{ fontSize: 12, fontWeight: 500, color: '#FF3B30', marginTop: 6 }}
                          >
                            {fieldError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Save button */}
            <div style={{ padding: '0 20px' }}>
              <button
                data-testid="btn-save-metrics"
                onClick={handleSave}
                disabled={!canSave}
                style={{
                  width: '100%',
                  height: 52,
                  borderRadius: 16,
                  border: 'none',
                  background: canSave ? 'linear-gradient(135deg,#1A3A5C,#4A6CF7)' : 'rgba(26,58,92,0.12)',
                  color: canSave ? 'white' : '#9AA5B4',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: canSave ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  fontFamily: F,
                  transition: 'background 0.3s',
                  boxShadow: canSave ? '0 6px 20px rgba(74,108,247,0.3)' : 'none',
                }}
              >
                {saving ? 'Сохраняем…' : 'Сохранить →'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
